import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  Alert,
  MenuItem,
  TablePagination,
} from "@mui/material";
import { ContextSession } from "../../../services/ContextSession";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ================= CONFIG ================= */

const API_ENCUESTAS = "https://bei360.injupemp.gob.hn:8444/api/Encuesta/";
const API_AGENTES = "https://chatbot.injupemp.gob.hn/obtener_agentes/";
const API_RNP = "https://bei360.injupemp.gob.hn:8444/api/rnp/nombreRNP/";

/* ================= CONFIGURACIÓN ================= */

const criteriosConfig = [
  { key: "saludo", label: "Saludo y cortesía", max: 10 },
  { key: "identificacion", label: "Identificación del cliente", max: 10 },
  { key: "escucha", label: "Escucha activa", max: 15 },
  { key: "resolucion", label: "Resolución del caso", max: 25 },
  { key: "objeciones", label: "Manejo de objeciones", max: 15 },
  { key: "cierre", label: "Cierre de la llamada", max: 10 },
  { key: "protocolo", label: "Cumplimiento del protocolo", max: 15 },
];

const retroConfig = [
  {
    key: "saludo",
    label: "El saludo y la cortesía del agente fueron adecuados",
    max: 20,
  },
  { key: "escucha", label: "Escucho y entendió su solicitud", max: 30 },
  { key: "solucion", label: "La solución brindad fue efectiva", max: 25 },
  { key: "general", label: "Como califica la atención en general", max: 25 },
];

/* ================= COMPONENTE ================= */

const EncuestasEncargadoPage = () => {
  const { sessionData } = useContext(ContextSession);
  const TOKEN = sessionData?.token;
  const USUARIO_REGISTRO = sessionData?.username || "Desconocido";
  const USUARIO_ID = sessionData?.id || "Desconocido";

  const [agenteUsuario, setAgenteUsuario] = useState(null);
  const [encuestas, setEncuestas] = useState([]);
  const [agentes, setAgentes] = useState([]);

  /* 🔹 FILTROS */
  const [filtroOficial, setFiltroOficial] = useState("");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroFechaFinal, setFiltroFechaFinal] = useState("");

  const [dni, setDni] = useState("");
  const [participante, setParticipante] = useState("");
  const [oficialSeleccionado, setOficialSeleccionado] = useState("");
  const [telefono, setTelefono] = useState("");

  const [criterios, setCriterios] = useState({});
  const [retro, setRetro] = useState({});

  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");

  /* 🔹 PAGINACIÓN */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ================= UTIL ================= */

  const total = (obj) =>
    Object.values(obj).reduce((a, b) => a + Number(b || 0), 0);

  const totalCriterios = total(criterios);
  const totalRetro = total(retro);
  const normalizarDigitos = (value) => String(value || "").replace(/\D/g, "");

  /* ================= DATA ================= */

  const cargarEncuestas = async () => {
    const res = await fetch(API_ENCUESTAS, {
      headers: { Authorization: `Bearer ${TOKEN}` },
    });
    const data = await res.json();
    setEncuestas(Array.isArray(data) ? data : data.results || []);
  };

  const cargarAgentes = async () => {
    const res = await fetch(API_AGENTES);
    const data = await res.json();

    const disponibles = (data.agentes || []).filter((a) => a.disponible);
    setAgentes(disponibles);

    const agente = disponibles.find((a) => String(a.id) === String(USUARIO_ID));
    if (agente) setAgenteUsuario(agente);
  };

  useEffect(() => {
    if (TOKEN) {
      cargarEncuestas();
      cargarAgentes();
    }
  }, [TOKEN]);

  /* ================= DNI ================= */

  const buscarNombrePorDni = async (dniValue) => {
    try {
      const res = await fetch(API_RNP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dni: dniValue }),
      });
      const data = await res.json();
      setParticipante(data?.nombre || "");
    } catch {
      setParticipante("");
    }
  };

  /* ================= HANDLERS ================= */

  const handleCriterio = (key, max, value) => {
    setCriterios((prev) => ({
      ...prev,
      [key]: value === "SI" ? max : 0,
    }));
  };

  const handleRetro = (key, max, value) => {
    setRetro((prev) => ({
      ...prev,
      [key]: value === "SI" ? max : 0,
    }));
  };

  /* ================= CREAR / EDITAR ================= */

  const abrirCrear = () => {
    setEditId(null);
    setDni("");
    setParticipante("");
    setOficialSeleccionado(agenteUsuario?.nombre || "");
    setTelefono("");
    setCriterios({});
    setRetro({});
    setError("");
    setOpenModal(true);
  };

  const abrirEditar = (e) => {
    setEditId(e.EncuestaId);
    setDni(e.DNI);
    setParticipante(e.NombreParticipante);
    setOficialSeleccionado(e.OficialNombre);
    setTelefono(e.Telefono);

    setCriterios({
      saludo: e.Criterio_Saludo,
      identificacion: e.Criterio_Identificacion,
      escucha: e.Criterio_Escucha,
      resolucion: e.Criterio_Resolucion,
      objeciones: e.Criterio_Objeciones,
      cierre: e.Criterio_Cierre,
      protocolo: e.Criterio_Protocolo,
    });

    setRetro({
      saludo: e.Retro_Saludo,
      escucha: e.Retro_Escucha,
      solucion: e.Retro_Solucion,
      general: e.Retro_General,
    });

    setOpenModal(true);
  };

  /* ================= GUARDAR ================= */

  const guardar = async () => {
    if (!/^\d{13}$/.test(dni)) return setError("El DNI debe tener 13 dígitos");
    if (!participante) return setError("No se encontró el participante");
    if (!oficialSeleccionado) return setError("Seleccione el oficial");
    if (!telefono) return setError("El Teléfono es obligatorio");

    const payload = {
      NombreParticipante: participante,
      DNI: dni,
      OficialNombre: oficialSeleccionado,
      Telefono: telefono,

      Criterio_Saludo: criterios.saludo || 0,
      Criterio_Identificacion: criterios.identificacion || 0,
      Criterio_Escucha: criterios.escucha || 0,
      Criterio_Resolucion: criterios.resolucion || 0,
      Criterio_Objeciones: criterios.objeciones || 0,
      Criterio_Cierre: criterios.cierre || 0,
      Criterio_Protocolo: criterios.protocolo || 0,

      Retro_Saludo: retro.saludo || 0,
      Retro_Escucha: retro.escucha || 0,
      Retro_Solucion: retro.solucion || 0,
      Retro_General: retro.general || 0,

      TotalCriterios: totalCriterios,
      TotalRetroalimentacion: totalRetro,
      UsuarioRegistro: USUARIO_REGISTRO,
    };

    const url = editId ? `${API_ENCUESTAS}${editId}/` : API_ENCUESTAS;
    const method = editId ? "PATCH" : "POST";

    await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${TOKEN}`,
      },
      body: JSON.stringify(payload),
    });

    setOpenModal(false);
    cargarEncuestas();
  };

  /* ================= FILTROS ================= */

  const fechasInvalidas =
    Boolean(filtroFechaInicio) &&
    Boolean(filtroFechaFinal) &&
    filtroFechaFinal < filtroFechaInicio;

  const encuestasFiltradas = encuestas
    .filter((e) => (filtroOficial ? e.OficialNombre === filtroOficial : true))
    .filter((e) => {
      if (!filtroBusqueda) return true;

      const busqueda = filtroBusqueda.trim().toLowerCase();
      const busquedaDigitos = normalizarDigitos(filtroBusqueda);
      const dni = String(e.DNI || "").toLowerCase();
      const telefono = String(e.Telefono || "").toLowerCase();
      const telefonoDigitos = normalizarDigitos(e.Telefono);

      return (
        dni.includes(busqueda) ||
        telefono.includes(busqueda) ||
        (busquedaDigitos &&
          (dni.includes(busquedaDigitos) ||
            telefonoDigitos.includes(busquedaDigitos)))
      );
    })
    .filter((e) => {
      if (!filtroFechaInicio) return true;
      const fechaRegistro = String(e.FechaRegistro || "").slice(0, 10);
      return fechaRegistro >= filtroFechaInicio;
    })
    .filter((e) => {
      if (!filtroFechaFinal) return true;
      const fechaRegistro = String(e.FechaRegistro || "").slice(0, 10);
      return fechaRegistro <= filtroFechaFinal;
    });

  const encuestasPaginadas = encuestasFiltradas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* ================= EXPORTAR EXCEL ================= */

  const descargarExcel = () => {
    const datos = encuestasFiltradas.map((e) => ({
      Fecha: e.FechaRegistro?.replace("T", " ").substring(0, 19),
      Participante: e.NombreParticipante,
      DNI: e.DNI,
      Telefono: e.Telefono,
      Oficial: e.OficialNombre,
      Total_Agente: e.TotalCriterios,
      Total_Auto: e.TotalRetroalimentacion,
    }));

    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Encuestas");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "encuestas.xlsx");
  };

  /* ================= UI ================= */

  return (
    <Box maxWidth={1400} mx="auto" mt={4}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5">Encuestas (Encargado)</Typography>

          <Box>
            <Button variant="outlined" sx={{ mr: 1 }} onClick={descargarExcel}>
              Descargar Excel
            </Button>

            <Button variant="contained" onClick={abrirCrear}>
              Nueva Encuesta
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Filtrar por DNI o Telefono"
              value={filtroBusqueda}
              onChange={(e) => {
                setFiltroBusqueda(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              type="date"
              fullWidth
              label="Fecha Inicio"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filtroFechaFinal || undefined }}
              value={filtroFechaInicio}
              onChange={(e) => {
                setFiltroFechaInicio(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              type="date"
              fullWidth
              label="Fecha Final"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filtroFechaInicio || undefined }}
              error={fechasInvalidas}
              helperText={
                fechasInvalidas
                  ? "Fecha Final no puede ser menor a Fecha Inicio"
                  : ""
              }
              value={filtroFechaFinal}
              onChange={(e) => {
                setFiltroFechaFinal(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Filtrar por Oficial"
              value={filtroOficial}
              onChange={(e) => {
                setFiltroOficial(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {agentes.map((a) => (
                <MenuItem key={a.id} value={a.nombre}>
                  {a.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Participante</TableCell>
                <TableCell>DNI</TableCell>
                <TableCell>Télefono</TableCell>
                <TableCell>Oficial</TableCell>
                <TableCell>Total Agente</TableCell>
                <TableCell>Total Auto</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {encuestasPaginadas.map((e) => (
                <TableRow key={e.EncuestaId}>
                  <TableCell>
                    {e.FechaRegistro?.replace("T", " ").substring(0, 19)}
                  </TableCell>
                  <TableCell>{e.NombreParticipante}</TableCell>
                  <TableCell>{e.DNI}</TableCell>
                  <TableCell>{e.Telefono}</TableCell>
                  <TableCell>{e.OficialNombre}</TableCell>
                  <TableCell>{e.TotalCriterios}</TableCell>
                  <TableCell>{e.TotalRetroalimentacion}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => abrirEditar(e)}>
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={encuestasFiltradas.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* ================= MODAL ================= */}
      <Dialog open={openModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? "Editar Encuesta" : "Nueva Encuesta"}
        </DialogTitle>

        <DialogContent>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <TextField
                label="DNI"
                fullWidth
                value={dni}
                inputProps={{ maxLength: 13 }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*$/.test(v)) {
                    setDni(v);
                    if (v.length === 13) buscarNombrePorDni(v);
                  }
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Nombre del participante"
                fullWidth
                value={participante}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Oficial que atendió"
                value={oficialSeleccionado}
                onChange={(e) => setOficialSeleccionado(e.target.value)}
              >
                {agentes.map((a) => (
                  <MenuItem key={a.id} value={a.nombre}>
                    {a.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Télefono Participante"
                fullWidth
                value={telefono}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => setTelefono(e.target.value)}
              />
            </Grid>
          </Grid>

          <Typography variant="h6">Evaluación</Typography>
          <Grid container spacing={2}>
            {criteriosConfig.map((c) => (
              <Grid item xs={6} key={c.key}>
                <TextField
                  select
                  fullWidth
                  label={c.label}
                  value={criterios[c.key] === c.max ? "SI" : "NO"}
                  onChange={(e) => handleCriterio(c.key, c.max, e.target.value)}
                >
                  <MenuItem value="SI">SI</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </TextField>
              </Grid>
            ))}
          </Grid>

          <Typography mt={2}>Total: {totalCriterios} / 100</Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6">Auto Evaluación</Typography>
          <Grid container spacing={2}>
            {retroConfig.map((r) => (
              <Grid item xs={6} key={r.key}>
                <TextField
                  select
                  fullWidth
                  label={r.label}
                  value={retro[r.key] === r.max ? "SI" : "NO"}
                  onChange={(e) => handleRetro(r.key, r.max, e.target.value)}
                >
                  <MenuItem value="SI">SI</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </TextField>
              </Grid>
            ))}
          </Grid>

          <Typography mt={2}>Total: {totalRetro} / 100</Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={() => setOpenModal(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardar}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncuestasEncargadoPage;
